import { Dispatch } from 'react';
import { FlowButton, FlowConnectorConfig, FlowItem } from '../../../../_components/flow-builder/TypeDefs';
import { colors } from '../../../../constants/FlowBuilder';
import { journeyCanvasConstants } from '../../../../constants/JourneyCanvas';
import { EvenSchemaDetailMap, FlowConnectorButtonActions, FlowItemDataMap, LoaderHook, ServiceTypes, ToastMessageHook, TranslateHook } from '../TypeDefs';
import { getAndMergeNodeIdsFromFlowItems, isCondExpForbiddenOnConnector, toFlowMergeRefs } from './AndMergeForkGraph';

import { getAssociatedServices, getEventSchema } from '../../../../store/_api';
import condExpIcon from '../../../../assets/images/CondExp.svg';
import deleteLine from '../../../../assets/images/DeleteLine.svg';
import eventSchemaIcon from '../../../../assets/images/EventSchema.svg';
import { Orientation } from '../../../../_components/flow-builder/canvas-element/Orientation';
import { Actions, UpdateActions } from '../../reducer/TypeDefs';
import { translateLabel } from './PrepareJourneyHelpers';
import externalEventConfirmationIcon from '../../../../assets/images/ExternalEventConfirmation.svg';

/**
 * @method generateTooltipTextForConnector
 * @description create tooltip text for connector
 * @param text tooltip text
 * @param mep mep is SYNC
 * @param external if node is external
 * @returns tooltip text
 */
export const generateTooltipTextForConnector = (text: string, mep: boolean, external: boolean) => {
  if (mep) {
    return `${text}\n${translateLabel('labels.mep_text')}`;
  }
  if (external) {
    return `${text}\nExternal`;
  }
  return text;
};

/**
 * @method getFlowConnectorConfig
 * @description Gets the flow connector configuration based on the presence of a conditional expression.
 * @param {Object} options - The options for getting the flow connector configuration.
 * @param {boolean} options.hasCondExpr - Indicates whether a conditional expression is present.
 * @param {FlowConnectorConfig} [options.config] - The existing flow connector configuration.
 * @returns {FlowConnectorConfig} - The flow connector configuration.
 */
export const getFlowConnectorConfig = ({
  hasCondExpr,
  external,
}: {
  hasCondExpr: boolean,
  external: boolean,
  mep: boolean,
}) => {
  const config: FlowConnectorConfig = {};
  if (hasCondExpr) {
    config.fillColor = colors.connectorExpression;
    config.selectedFillColor = colors.connectorExpression;
    config.clickShape = 'diamond';
  }
  if (external) {
    config.strokeStyle = 'dash';
  }

  return config;
};

/**
 * @method getFlowConnectorButtons
 * @description Gets the flow connector buttons based on the converging flag.
 * @param {boolean} converging - Indicates whether the flow is converging.
 * @param {boolean} readOnly - if canvas is in view mode
 * @param {boolean} allowConditionalExpression - when false (AND merge), hide conditional expression control
 * @returns {FlowButton[]} - An array of flow connector buttons.
 */
export const getFlowConnectorButtons = (
  converging: boolean,
  readOnly: boolean,
  isBusinessToBusiness: boolean,
  isDesigningCEM: boolean,
  allowConditionalExpression = true
): FlowButton[] => {
  const btns: FlowButton[] = [];
  if (allowConditionalExpression) {
    btns.push({
      icon: condExpIcon,
      id: FlowConnectorButtonActions.COND_EXP,
      label: translateLabel('labels.cond_exp'),
      height: journeyCanvasConstants.flowItemBtnHeight,
      width: journeyCanvasConstants.flowItemBtnHeight * (19 / 16),
    });
  }
  if (converging && !readOnly) {
    btns.push({
      icon: deleteLine,
      id: FlowConnectorButtonActions.DELETE,
      label: translateLabel('labels.delete'),
      height: journeyCanvasConstants.flowItemBtnHeight,
      width: journeyCanvasConstants.flowItemBtnHeight,
    });
  }
  if (isBusinessToBusiness && !readOnly && !isDesigningCEM) {
    btns.push({
      icon: externalEventConfirmationIcon,
      id: FlowConnectorButtonActions.EXTERNAL,
      label: translateLabel('labels.external_event_confirmation'),
      height: journeyCanvasConstants.flowItemMepBtnHeight,
      width: journeyCanvasConstants.flowItemBtnHeight,
    });
  }
  btns.push({
    icon: eventSchemaIcon,
    id: FlowConnectorButtonActions.VIEW_EVENT_SCHEMA,
    label: translateLabel('labels.view_event_schema'),
    height: journeyCanvasConstants.flowItemBtnHeight,
    width: journeyCanvasConstants.flowItemBtnHeight,
  });
  return btns;
};

/**
 * @method connectorBtnClickHandler
 * @description handles delete button on connector line
 * @param btnId
 * @param connector
 */
export const connectorBtnClickHandler = (btnId: string, connector: { to: string, from: string }, dispatch: Dispatch<UpdateActions>, eventSchemaDetailMap: EvenSchemaDetailMap, flowItemDataMap: FlowItemDataMap, toastMessage: ToastMessageHook, translate: TranslateHook, flowItems: FlowItem[] = [], flowConnectors: { fromNodeId: string, toNodeId: string }[] = []) => {
  (async () => {
  if (btnId === FlowConnectorButtonActions.DELETE) {
    dispatch({ type: Actions.NOTIFY_DELETE_CONNECTOR, payload: connector });
  } else if (btnId === FlowConnectorButtonActions.COND_EXP) {
    const andMergeNodeIds = getAndMergeNodeIdsFromFlowItems(flowItems);
    if (isCondExpForbiddenOnConnector(connector.from, connector.to, flowConnectors, andMergeNodeIds, toFlowMergeRefs(flowItems))) {
      return;
    }
    dispatch({ type: Actions.SHOW_COND_EXP_MODAL, payload: { toNodeId: connector.to, fromNodeId: connector.from } });
    const toNode = flowItemDataMap[connector.to];
    if (toNode.ingressEvent && toNode.ingressEventVersion) {
      const key = `${toNode.ingressEvent}@${toNode.ingressEventVersion}`;
      const eventSchema = eventSchemaDetailMap[key] ?? await getEventSchema(toNode.ingressEvent, toNode.ingressEventVersion);
      if (eventSchema) {
        dispatch({ type: Actions.SET_EVENT_SCHEMA, payload: { key, eventSchema } });
      } else {
        // if event schema is not found or getEventSchema fails, clear the event schema and show error
        dispatch({ type: Actions.CLEAR_EVENT_SCHEMA });
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_toast_body_error'));
      }
    }
  } else if (btnId === FlowConnectorButtonActions.EXTERNAL) {
    dispatch({ type: Actions.SHOW_EXTERNAL_EVENT_CONFIRMATION_MODAL, payload: { toNodeId: connector.to, fromNodeId: connector.from } });
  } else if (btnId === FlowConnectorButtonActions.VIEW_EVENT_SCHEMA) {
    const toNode = flowItemDataMap[connector.to];
    if (toNode.ingressEvent && toNode.ingressEventVersion) {
      const key = `${toNode.ingressEvent}@${toNode.ingressEventVersion}`;
      const eventSchema = eventSchemaDetailMap[key] ?? await getEventSchema(toNode.ingressEvent, toNode.ingressEventVersion);
      dispatch({ type: Actions.HIDE_COND_EXP_MODAL });
      if (eventSchema) {
        dispatch({ type: Actions.SET_EVENT_SCHEMA, payload: { key, eventSchema } });
      } else {
        // if event schema is not found or getEventSchema fails, clear the event schema and show error
        dispatch({ type: Actions.CLEAR_EVENT_SCHEMA });
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_toast_body_error'));
      }
    }
  }
  })();
};

/**
 * @method connectorSelectHandler
 * @description selecting connector on canvas
 * @param connector
 */
export const connectorSelectHandler = (dispatch: Dispatch<UpdateActions>, connector?: {
  from: string;
  to: string;
}) => {
  // connector is selected, record it
  if (connector) {
    dispatch({
      type: Actions.SELECT_CONNECTOR,
      payload: { to: connector.to, from: connector.from }
    });
  } else {
    dispatch({ type: Actions.SELECT_CONNECTOR });
  }
};

/**
 * @method connectorDrawHandler
 * @description Drawing connector on canvas
 * @param from
 * @param to
 * @param orientation
 */
export const connectorDrawHandler = (
  {
    from,
    to,
    orientation,
    loader,
    dispatch,
    flowItemDataMap
  }: {
    from: string,
    to: string,
    orientation: { from: Orientation; to: Orientation },
    loader: LoaderHook,
    dispatch: Dispatch<UpdateActions>,
    flowItemDataMap: FlowItemDataMap
  }
) => {
  // flow builder is trying to make a connection between 2 events, verify
  // if the connector can be drawn and then add the from events idf to `connectors` of to events
  (async () => {
    const fromService = flowItemDataMap[from];
    const toService = flowItemDataMap[to];
    if (fromService && toService) {
      loader.show();
      const associatedServices = await getAssociatedServices(fromService.serviceVersion, fromService.serviceName, fromService.ingressEvent, fromService.ingressEventVersion, fromService.serviceOperationName);

      if (associatedServices) {
        const serviceOperation = associatedServices.find(
          (data) =>
            data.serviceName === toService.serviceName
            && data.version === toService.serviceVersion
        )?.serviceOperations?.find(
          (op) =>
            op.serviceOperation === toService.serviceOperationName
        );
        const ingressEvent = serviceOperation?.ingressEvents?.find(
          (ev) =>
            ev.ingressEventName === toService.ingressEvent
            && Number(ev.ingressEventVersion) === toService.ingressEventVersion
        );

        if (ingressEvent) {
          dispatch({
            type: Actions.CONNECTOR_DRAW,
            payload: { to, from, orientation, name: ingressEvent.ingressEventName }
          });
        }
      } else {
        dispatch({ type: Actions.SIDEBAR_DATA_ERROR });
      }
      loader.hide();
    }
  })();
};

/**
 * @method egressClickHandler
 * @description Handles the click event for an egress element.
 * @param {string} id - The ID of the clicked element.
 * @param {'element' | 'step'} type - The type of the clicked element.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {boolean} designingStep - Indicates whether the step is being designed.
 */
export const egressClickHandler = (
  {
    id,
    type,
    dispatch,
    designingStep
  }: {
    id: string,
    type: 'element' | 'step',
    dispatch: Dispatch<UpdateActions>,
    designingStep: boolean
  }
) => {
  if (type === 'element' && !designingStep) {
    dispatch({
      type: Actions.CLICK_STEP_CONNECTOR_ICON,
      payload: {
        prevNodeId: id
      }
    });
  }
};

/**
 * @method deleteConnector
 * @description deleting the connector on canvas
 */
export const deleteConnector = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.DELETE_CONNECTOR });
};

/**
 * @method checkBusinessToBusinessConnector
 * @description check if connector is having both from and to node with service type as Business
 */
export const checkBusinessToBusinessConnector = (fromServiceType: string, toServiceType: string) => {
  if (fromServiceType !== toServiceType) {
    return false;
  }

  const nonBusinessTypes: string[] = [
    ServiceTypes.EXTERNAL,
    ServiceTypes.MQ,
    ServiceTypes.DB2,
    ServiceTypes.SOAP,
  ];

  return !nonBusinessTypes.includes(fromServiceType);
};
